import { NetworkRequestData } from '../types'

import {
  DEVTOOLS_HTTP_STATUS_CLIENT_ERROR_MIN,
  DEVTOOLS_NETWORK_MAX_BODY_LOG_SIZE,
} from './constants'
import { requestTracker } from './requestTracker'

/* eslint-disable security/detect-object-injection -- DevTools network logger copies arbitrary header/query/form keys */
const PROCESSOR_URL_PATTERN = /\/api\/processors?\/([^/?]+)/
const TRACE_ID_HEADERS = ['x-request-id', 'x-trace-id', 'traceparent'] as const

// Dynamic import for axios to avoid issues if not available
let axiosInstance: typeof import('axios').default | null = null

try {
  // eslint-disable-next-line @typescript-eslint/no-require-imports, @typescript-eslint/no-var-requires -- optional peer; sync load for interceptor
  const axiosModule = require('axios')
  axiosInstance = axiosModule.default ?? axiosModule
} catch {
  // Axios not available, skip axios interceptor
}

// ============================================================================
// Utility Functions
// ============================================================================

/**
 * Extract processor name from URL
 * Examples:
 * - /api/processor/getUserData -> getUserData
 * - /api/processors/getUserData -> getUserData
 * - /api/processors/finances_financesProcessor?locale=pt_BR -> finances_financesProcessor
 */
function extractProcessorName(url: string): string | null {
  try {
    const urlObj = new URL(url, window.location.origin)
    const match = urlObj.pathname.match(PROCESSOR_URL_PATTERN)
    return match ? match[1] : null
  } catch {
    // Fallback for relative URLs
    const match = url.match(PROCESSOR_URL_PATTERN)
    return match ? match[1] : null
  }
}

/**
 * Extract query parameters from URL
 */
function extractQueryParams(url: string): Record<string, string> {
  try {
    const urlObj = new URL(url, window.location.origin)
    const params: Record<string, string> = {}
    urlObj.searchParams.forEach((value, key) => {
      params[key] = value
    })
    return params
  } catch {
    return {}
  }
}

/**
 * Extract trace ID from headers
 */
function extractTraceId(headers: Record<string, string>): string | undefined {
  for (const headerName of TRACE_ID_HEADERS) {
    if (headers[headerName]) {
      return headers[headerName]
    }
  }
  return undefined
}

/**
 * Normalize headers from various formats
 */
function normalizeHeaders(
  headers: Headers | Record<string, string> | Array<[string, string]> | undefined
): Record<string, string> {
  if (!headers) return {}

  if (headers instanceof Headers) {
    const result: Record<string, string> = {}
    headers.forEach((value, key) => {
      result[key] = value
    })
    return result
  }

  if (Array.isArray(headers)) {
    const result: Record<string, string> = {}
    headers.forEach(([key, value]) => {
      result[key] = value
    })
    return result
  }

  return headers
}

/**
 * Parse JSON with fallback to original value
 */
function safeJsonParse<T>(text: string, fallback: T): T {
  try {
    return JSON.parse(text) as T
  } catch {
    return fallback
  }
}

/**
 * Clone and parse body with size limit
 */
function parseBodyWithLimit(body: string): unknown {
  if (body.length > DEVTOOLS_NETWORK_MAX_BODY_LOG_SIZE) {
    return { _truncated: true, _size: body.length }
  }
  return safeJsonParse(body, body)
}

/**
 * Clone request body for logging (with size limit)
 */
function cloneRequestBody(body: BodyInit | null): unknown {
  if (!body) return null

  try {
    if (typeof body === 'string') {
      return parseBodyWithLimit(body)
    }

    if (body instanceof FormData || body instanceof URLSearchParams) {
      const entries: Record<string, string> = {}
      for (const [key, value] of body.entries()) {
        entries[key] = String(value)
      }
      return entries
    }

    if (body instanceof Blob) {
      return { _type: 'blob', _size: body.size }
    }

    if (body instanceof ArrayBuffer) {
      return { _type: 'arraybuffer', _size: body.byteLength }
    }

    return null
  } catch {
    return null
  }
}

/**
 * Clone response body for logging (with size limit)
 */
async function cloneResponseBody(response: Response): Promise<unknown> {
  try {
    const contentType = response.headers.get('content-type') || ''
    if (!contentType.includes('application/json')) {
      return null
    }

    const cloned = response.clone()
    const text = await cloned.text()
    return parseBodyWithLimit(text)
  } catch {
    return null
  }
}

/**
 * Generate unique ID for request
 */
function generateRequestId(): string {
  return `${Date.now()}-${crypto.randomUUID()}`
}

/**
 * Normalize HTTP method to uppercase
 */
function normalizeMethod(
  method: string | undefined,
  defaultMethod = 'GET'
): NetworkRequestData['method'] {
  return (method || defaultMethod).toUpperCase() as NetworkRequestData['method']
}

/**
 * Create error object from error instance
 */
function createErrorObject(err: unknown): NetworkRequestData['error'] {
  if (err instanceof Error) {
    return {
      message: err.message,
      stack: err.stack,
      code: 'code' in err ? String(err.code) : undefined,
    }
  }
  return {
    message: String(err),
  }
}

/**
 * Build NetworkRequestData object
 */
function buildNetworkRequest(params: {
  id: string
  processorName: string
  method: NetworkRequestData['method']
  url: string
  statusCode: number
  responseTime: number
  timestamp: string
  queryParams?: Record<string, string>
  headers?: Record<string, string>
  requestBody?: unknown
  responseBody?: unknown
  requestTraceId?: string
  error?: NetworkRequestData['error']
}): NetworkRequestData {
  return {
    id: params.id,
    processorName: params.processorName,
    method: params.method,
    url: params.url,
    statusCode: params.statusCode,
    responseTime: params.responseTime,
    timestamp: params.timestamp,
    queryParams:
      params.queryParams && Object.keys(params.queryParams).length > 0
        ? params.queryParams
        : undefined,
    headers:
      params.headers && Object.keys(params.headers).length > 0
        ? params.headers
        : undefined,
    requestBody: params.requestBody,
    responseBody: params.responseBody,
    requestTraceId: params.requestTraceId,
    error: params.error,
  }
}

// ============================================================================
// Fetch Interceptor
// ============================================================================

/**
 * Intercept fetch requests to /api/processor/* and /api/processors/*
 */
function interceptFetch(): void {
  const originalFetch = window.fetch

  window.fetch = async function (
    input: RequestInfo | URL,
    init?: RequestInit
  ): Promise<Response> {
    const url =
      typeof input === 'string' ? input : input instanceof URL ? input.href : input.url
    const processorName = extractProcessorName(url)

    if (!processorName) {
      return originalFetch(input, init)
    }

    const startTime = performance.now()
    const requestId = generateRequestId()
    const timestamp = new Date().toISOString()
    const method = normalizeMethod(init?.method)
    const queryParams = extractQueryParams(url)
    const headers = normalizeHeaders(init?.headers)
    const requestBody = cloneRequestBody(init?.body || null)
    const requestTraceId = extractTraceId(headers)

    try {
      const response = await originalFetch(input, init)
      const responseTime = Math.round(performance.now() - startTime)
      const responseBody = await cloneResponseBody(response)

      requestTracker.addRequest(
        buildNetworkRequest({
          id: requestId,
          processorName,
          method,
          url,
          statusCode: response.status,
          responseTime,
          timestamp,
          queryParams,
          headers,
          requestBody,
          responseBody,
          requestTraceId,
        })
      )

      return response
    } catch (err) {
      const responseTime = Math.round(performance.now() - startTime)

      requestTracker.addRequest(
        buildNetworkRequest({
          id: requestId,
          processorName,
          method,
          url,
          statusCode: 0,
          responseTime,
          timestamp,
          queryParams,
          headers,
          requestBody,
          responseBody: null,
          requestTraceId,
          error: createErrorObject(err),
        })
      )

      throw err
    }
  }
}

// ============================================================================
// XMLHttpRequest Interceptor
// ============================================================================

interface XHRRequestInfo {
  processorName: string
  method: string
  url: string
  startTime: number
  requestId: string
  timestamp: string
  queryParams: Record<string, string>
  requestBody: unknown
}

/**
 * Extract response headers from XMLHttpRequest
 */
function extractXHRHeaders(xhr: XMLHttpRequest): Record<string, string> {
  const headers: Record<string, string> = {}
  try {
    const headerString = xhr.getAllResponseHeaders()
    if (headerString) {
      headerString.split('\r\n').forEach((line) => {
        const [key, ...valueParts] = line.split(':')
        if (key && valueParts.length > 0) {
          headers[key.trim()] = valueParts.join(':').trim()
        }
      })
    }
  } catch {
    // Ignore header extraction errors
  }
  return headers
}

/**
 * Extract response body from XMLHttpRequest
 */
function extractXHRResponseBody(xhr: XMLHttpRequest): unknown {
  try {
    const responseText = xhr.responseText
    if (!responseText) return null
    return parseBodyWithLimit(responseText)
  } catch {
    return null
  }
}

/**
 * Intercept XMLHttpRequest requests to /api/processor/* and /api/processors/*
 */
function interceptXMLHttpRequest(): void {
  const originalOpen = XMLHttpRequest.prototype.open
  const originalSend = XMLHttpRequest.prototype.send

  XMLHttpRequest.prototype.open = function (
    method: string,
    url: string | URL,
    ...args: unknown[]
  ): void {
    const urlString = typeof url === 'string' ? url : url.href
    const processorName = extractProcessorName(urlString)

    if (processorName) {
      const xhr = this as XMLHttpRequest & {
        __devToolRequestInfo?: XHRRequestInfo
      }
      xhr.__devToolRequestInfo = {
        processorName,
        method: method.toUpperCase(),
        url: urlString,
        startTime: performance.now(),
        requestId: generateRequestId(),
        timestamp: new Date().toISOString(),
        queryParams: extractQueryParams(urlString),
        requestBody: null,
      }
    }

    return originalOpen.apply(this, [method, url, ...args] as [
      string,
      string | URL,
      boolean?,
      string?,
      string?
    ])
  }

  XMLHttpRequest.prototype.send = function (
    body?: Document | XMLHttpRequestBodyInit | null
  ): void {
    const xhr = this as XMLHttpRequest & {
      __devToolRequestInfo?: XHRRequestInfo
    }
    const requestInfo = xhr.__devToolRequestInfo

    if (!requestInfo) {
      return originalSend.apply(this, [body])
    }

    // Extract request body
    requestInfo.requestBody = body
      ? typeof body === 'string'
        ? safeJsonParse(body, body)
        : body instanceof FormData || body instanceof URLSearchParams
        ? (() => {
            const entries: Record<string, string> = {}
            for (const [key, value] of body.entries()) {
              entries[key] = String(value)
            }
            return entries
          })()
        : { _type: typeof body }
      : null

    const handleResponse = (): void => {
      const responseTime = Math.round(performance.now() - requestInfo.startTime)
      const statusCode = xhr.status || 0
      const headers = extractXHRHeaders(xhr)
      const responseBody = extractXHRResponseBody(xhr)
      const requestTraceId = extractTraceId(headers)

      const error: NetworkRequestData['error'] | undefined =
        statusCode >= DEVTOOLS_HTTP_STATUS_CLIENT_ERROR_MIN || statusCode === 0
          ? {
              message: xhr.statusText || 'Request failed',
              code: statusCode.toString(),
            }
          : undefined

      requestTracker.addRequest(
        buildNetworkRequest({
          id: requestInfo.requestId,
          processorName: requestInfo.processorName,
          method: normalizeMethod(requestInfo.method),
          url: requestInfo.url,
          statusCode,
          responseTime,
          timestamp: requestInfo.timestamp,
          queryParams: requestInfo.queryParams,
          headers,
          requestBody: requestInfo.requestBody,
          responseBody,
          requestTraceId,
          error,
        })
      )
    }

    xhr.addEventListener('loadend', handleResponse, { once: true })
    return originalSend.apply(this, [body])
  }
}

// ============================================================================
// Axios Interceptor
// ============================================================================

interface AxiosRequestInfo {
  processorName: string
  startTime: number
  requestId: string
  timestamp: string
}

/**
 * Normalize axios headers (can be arrays or strings)
 */
function normalizeAxiosHeaders(
  headers: Record<string, unknown> | undefined
): Record<string, string> {
  if (!headers) return {}

  const result: Record<string, string> = {}
  Object.keys(headers).forEach((key) => {
    const value = headers[key]
    if (value) {
      result[key] = Array.isArray(value) ? value.join(', ') : String(value)
    }
  })
  return result
}

/**
 * Extract request body from axios config
 */
function extractAxiosRequestBody(data: unknown): unknown {
  if (!data) return null

  if (typeof data === 'string') {
    return safeJsonParse(data, data)
  }

  if (data instanceof FormData) {
    const entries: Record<string, string> = {}
    for (const [key, value] of data.entries()) {
      entries[key] = String(value)
    }
    return entries
  }

  return data
}

/**
 * Extract response body from axios response
 */
function extractAxiosResponseBody(data: unknown): unknown {
  if (typeof data === 'string') {
    return safeJsonParse(data, data)
  }
  return data
}

/**
 * Intercept axios requests to /api/processor/* and /api/processors/*
 */
function interceptAxios(): (() => void) | null {
  if (!axiosInstance) {
    return null
  }

  const requestInterceptorId = axiosInstance.interceptors.request.use(
    (config) => {
      const url = config.url || ''
      const processorName = extractProcessorName(url)

      if (processorName) {
        const axiosConfig = config as typeof config & {
          __devToolRequestInfo?: AxiosRequestInfo
        }
        axiosConfig.__devToolRequestInfo = {
          processorName,
          startTime: performance.now(),
          requestId: generateRequestId(),
          timestamp: new Date().toISOString(),
        }
      }

      return config
    },
    (error) => Promise.reject(error)
  )

  const responseInterceptorId = axiosInstance.interceptors.response.use(
    (response) => {
      const axiosConfig = response.config as typeof response.config & {
        __devToolRequestInfo?: AxiosRequestInfo
      }
      const requestInfo = axiosConfig.__devToolRequestInfo

      if (!requestInfo) {
        return response
      }

      const url = response.config.url || ''
      const method = normalizeMethod(response.config.method, 'GET')
      const responseTime = Math.round(performance.now() - requestInfo.startTime)
      const requestHeaders = normalizeAxiosHeaders(
        response.config.headers as Record<string, unknown>
      )
      const responseHeaders = normalizeAxiosHeaders(
        response.headers as Record<string, unknown>
      )
      const requestTraceId =
        extractTraceId(requestHeaders) || extractTraceId(responseHeaders)
      const queryParams = extractQueryParams(url)
      const requestBody = extractAxiosRequestBody(response.config.data)
      const responseBody = extractAxiosResponseBody(response.data)

      requestTracker.addRequest(
        buildNetworkRequest({
          id: requestInfo.requestId,
          processorName: requestInfo.processorName,
          method,
          url,
          statusCode: response.status,
          responseTime,
          timestamp: requestInfo.timestamp,
          queryParams,
          headers: requestHeaders,
          requestBody,
          responseBody,
          requestTraceId,
        })
      )

      return response
    },
    (error) => {
      const axiosConfig = error.config as typeof error.config & {
        __devToolRequestInfo?: AxiosRequestInfo
      }
      const requestInfo = axiosConfig?.__devToolRequestInfo

      if (requestInfo) {
        const url = error.config?.url || ''
        const method = normalizeMethod(error.config?.method, 'GET')
        const responseTime = Math.round(performance.now() - requestInfo.startTime)
        const requestHeaders = normalizeAxiosHeaders(
          error.config?.headers as Record<string, unknown>
        )
        const requestTraceId = extractTraceId(requestHeaders)
        const queryParams = extractQueryParams(url)
        const requestBody = extractAxiosRequestBody(error.config?.data)
        const responseBody = error.response?.data || null

        requestTracker.addRequest(
          buildNetworkRequest({
            id: requestInfo.requestId,
            processorName: requestInfo.processorName,
            method,
            url,
            statusCode: error.response?.status || 0,
            responseTime,
            timestamp: requestInfo.timestamp,
            queryParams,
            headers: requestHeaders,
            requestBody,
            responseBody,
            requestTraceId,
            error: {
              message: error.message || 'Request failed',
              code: error.code,
              stack: error.stack,
            },
          })
        )
      }

      return Promise.reject(error)
    }
  )

  return () => {
    axiosInstance?.interceptors.request.eject(requestInterceptorId)
    axiosInstance?.interceptors.response.eject(responseInterceptorId)
  }
}

// ============================================================================
// Initialization
// ============================================================================

/**
 * Initialize network interceptor
 * Intercepts fetch, XMLHttpRequest, and axios calls to /api/processor/* and /api/processors/*
 */
export function initializeNetworkInterceptor(): () => void {
  console.log(
    '🚀 Network Interceptor initialized - monitoring /api/processor/* and /api/processors/* calls'
  )

  // Store original functions for cleanup
  const originalFetch = window.fetch
  const originalXHROpen = XMLHttpRequest.prototype.open
  const originalXHRSend = XMLHttpRequest.prototype.send

  // Setup interceptors
  interceptFetch()
  interceptXMLHttpRequest()
  const axiosCleanup = interceptAxios()

  const interceptors = ['Fetch', 'XHR', ...(axiosCleanup ? ['Axios'] : [])]
  console.log(
    `✅ Network Interceptor ready - ${interceptors.join(', ')} interceptors active`
  )

  // Return cleanup function
  return () => {
    console.log('🛑 Network Interceptor disabled')
    window.fetch = originalFetch
    XMLHttpRequest.prototype.open = originalXHROpen
    XMLHttpRequest.prototype.send = originalXHRSend
    axiosCleanup?.()
  }
}
